import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { PlayService } from '../../services/play.service';
import { Sound } from '../../services/sound';
import { Fret } from '../../services/chord-patterns/chord-patterns.service';
import { NotesService } from '../../services/notes.service';
import { ScalesService } from '../../services/scales.service';
import { ChordsService } from '../../services/chords.service';
import { StringsService } from '../../services/strings.service';
import { TuningsService } from '../../services/tunings.service';
import { StrictCS } from '../../services/model';

class FretZone {
  'note': number;
  'note12': number;
  'display': string;
  'cssNote': any;
  'cssHighlight': any;
  'cssClickable': any;
  cssDot: any;
}
export class HighlightFret {
  string!: number;
  fret!: number;
}
export class FretOnString {
  str!: number;
  fret!: number;
}
class FretNumber {
  'css': any;
}
interface DetuneString {
  'css': any;
  'tunerCss': any;
  'value': number;
  'display': string;
}
export interface ClickedNote {
  note: number;
  str: number;
  fretNumber: number;
}

@Component({
  selector: 'fretboard',
  templateUrl: './fretboards.component.html',
  styleUrls: ['./fretboards.component.scss']
})
export class FretboardsComponent implements OnInit {

  public chordColor = 'rgba(96,96,144,1)';
  public scaleColor = 'rgba(96,114,114,1)';
  public rootColor = 'rgba(144,96,96,1)';
  public scrollingNoClick = false;
  public scrollingNoClickTimer: any;

  @Input() title = '';
  @Input() numberOfFrets = 15;
  @Input() stringHeight = 25;
  @Input() fretSize = 104;
  @Input() upperLabels = 0;
  @Input() cssForFretboard = {};
  @Input() displayNotesOrIntervals = 'notes'; // Can be 'notes' or 'intervals'
  @Input()
  sound!: Sound;
  @Input() noTouch = false; // No touch event, just mouse down

  public frets: FretZone[][] = [];
  public fretNumbers: FretNumber[] = [];
  public fretboardCss!: any;
  public instruCss!: {};
  public lnbStrings = 6;
  public ltuning = [28, 33, 38, 43, 47, 52];
  public ldefaultTuning = [28, 33, 38, 43, 47, 52];
  public lmulti = 1;
  public ldoremi = false;
  public llefty = false;
  public lwithNotes = true;
  public lclickable = false;
  public lvolume = 1;
  public lhighlightString = -1;
  public lhighlightFrets: HighlightFret[] = [];
  public origin = 'left';
  private playTimers: any[] = [];
  private touchSupported = false;
  public pressed = -1; // string x fret currently pressed note

  // Tuner parameters: Only for standard 6 strings guitar. Use params.openTuning6 to set a guitar tuning
  public detuneStrings: DetuneString[] = [];
  public lwithTuners = false;
  public lwithDetuneStrings = false;
  public tunerWidth = 120;
  public detuneWidth = 40;
  @Input() set volume(v: number) {
    this.lvolume = v;
  }
  @Input() set displayParams(
    p: {
      nbStrings?: number;
      multi?: number;
      doremi?: boolean;
      lefty?: boolean;
      withNotes?: boolean;
      clickable?: boolean;
      tuning?: number[];
      defaultTuning?: number[];
      // Tuner parameters. Do not specify 'tuning' property with openTunings6 and withTuners or withDetuneStrings
      withTuners?: boolean;
      withDetuneStrings?: boolean;
      openTuning6?: string;
      highlightString?: number;
      highlightFrets?: HighlightFret[];
    }) {
    if (typeof p.nbStrings !== 'undefined') {
      this.lnbStrings = p.nbStrings;
    }
    if (typeof p.multi !== 'undefined') {
      this.lmulti = p.multi;
    }
    if (typeof p.doremi !== 'undefined') {
      this.ldoremi = p.doremi;
    }
    if (typeof p.lefty !== 'undefined') {
      this.llefty = p.lefty;
    }
    if (typeof p.withNotes !== 'undefined') {
      this.lwithNotes = p.withNotes;
    }
    if (typeof p.clickable !== 'undefined') {
      this.lclickable = p.clickable;
    }
    if (typeof p.tuning !== 'undefined') {
      this.ltuning = p.tuning;
    } else {
      this.ltuning = this.forceDefaultTuning();
    }
    if (typeof p.defaultTuning !== 'undefined') {
      this.ldefaultTuning = p.defaultTuning;
    } else {
      this.ldefaultTuning = this.forceDefaultTuning();
    }
    this.initStringTabs();
    if (typeof p.withTuners !== 'undefined') {
      this.lwithTuners = p.withTuners;
    }
    if (typeof p.withDetuneStrings !== 'undefined') {
      this.lwithDetuneStrings = p.withDetuneStrings;
    }
    if (typeof p.highlightString !== 'undefined') {
      this.lhighlightString = p.highlightString;
    }
    if (typeof p.highlightFrets !== 'undefined') {
      this.lhighlightFrets = p.highlightFrets;
    }
    if (typeof p.openTuning6 !== 'undefined') {
      const t: { values: number[], detunes: number[] } = this.tunings.getTuningNamesToValues(p.openTuning6, 'guitar', 6);
      this.ltuning = t.values;
      t.detunes.forEach((detune, i) => {
        this.detuneStrings[i].value = detune;
        this.detuneStrings[i].display = this.getDetuneDisplay(this.detuneStrings[i].value);
      });
    }
    this.generateFretboardElements();
  }
  public lhighlightNotes12: number[] = [];
  @Input() set highlightNotes12(notes12: number[]) {
    this.lhighlightNotes12 = notes12;
  }
  @Input() set highlightCS(p: StrictCS | undefined) {
    this.lhighlightNotes12 = [];
    if (!p) {
      return;
    }
    let structure;
    if (p.type === 'chord') {
      structure = this.chordsService.getStructure(p.cs);
    } else if (p.type === 'scale') {
      structure = this.scalesService.getStructure(p.cs);
    } else {
      return;
    }
    structure.forEach((offset: any) => {
      this.lhighlightNotes12.push((p.note12 + offset + 240) % 12);
    });
  }
  @Input() tunerOptionLabels = ['Reset', 'Select from known tuning', 'Close']; // Send an empty label '' to hide a button
  // Select parameters
  @Input() withToggle = false;

  @Input() selectOneFretByString = false;
  public noteSelected: number[][] = [];  // noteSelected[string][fret] = note or -1
  private resetInProgress = false;
  @Input() set reset(useless: boolean) {
    this.initStringTabs();
    this.generateFretboardElements();
  }

  // Chords / scales parameters for static display (not on click)
  private CSNote12 = -1;
  private CSName = '';
  private CSType = '';
  private diatonicScale = false;
  private diatonicNotes: string[] = [];
  public withAStartNoteName = ''; // For diatonic scale only, define the first note name ==> A# is possible!
  public isWithAStartNoteName = false;

  @Input() set displayChordScale(CS: {note12: number, type: string, name: string, withAStartNoteName?: string}) {
    // Avoid refresh if just type change
    const noteChanged = CS.note12 !== this.CSNote12;
    const typeChanged = CS.type !== this.CSType;
    const nameChanged = CS.name !== this.CSName;
    if (noteChanged || typeChanged || nameChanged) {
      this.CSType = CS.type;
      this.CSName = CS.name;
      this.CSNote12 = CS.note12;
      if (typeof CS.withAStartNoteName !== 'undefined') {
        this.withAStartNoteName = CS.withAStartNoteName;
        this.isWithAStartNoteName = (this.withAStartNoteName !== '');
      }
      this.generateFretboardElements();
    }
  }
  public note12Display: boolean[] = [
    true, true, true, true, true, true,
    true, true, true, true, true, true
  ];

  @Input() set toggleDisplayAllNotes(display: boolean) {
    for (let note12 = 0; note12 < 12; note12++) {
      this.note12Display[note12] = display;
    }
  }
  public intervalsDisplay!: string[];
  // ===========================================================
  //         Dynamic chords and scales display (on click)
  // ===========================================================
  @Input() bpm = 120;
  @Input() arpeggioChord = true;
  public lastClickedNote: ClickedNote = {
    note: -1,
    fretNumber: -1,
    str: -1
  };
  @Input() autoPlayNote12 = -1;
  @Input() autoPlayType = 'none';
  @Input() autoPlay = false;
  public autoPlayCS = '';
  @Input() set playCS(cs: { name: string, note12: number }) {
    if ((typeof cs === 'undefined') ||
    (typeof cs.name === 'undefined') ||
    (typeof cs.note12 === 'undefined') ||
    (!this.autoPlay)) {
      return;
    }
    if (this.autoPlayCS !== cs.name) {
      this.autoPlayCS = cs.name;
      if ((this.autoPlayCS !== '') &&
        (cs.note12 !== -1) &&
        (this.autoPlayType !== 'none')
      ) {
        if (cs.note12 === this.lastClickedNote.note % 12) {
          this.noteClick(
            this.lastClickedNote.note,
            this.lastClickedNote.str,
            this.lastClickedNote.fretNumber,
            false,
            false
          );
        } else {
          this.note12Click(cs.note12);
        }
      }
    }
  }
  @Input() set forcePlay(value: number) { // Used in chords for inversion change
    if ((value > 0) &&
      (this.autoPlay) &&
      (this.lastClickedNote.note !== -1) &&
      (this.autoPlayType !== 'none')
    ) {
      this.noteClick(
        this.lastClickedNote.note,
        this.lastClickedNote.str,
        this.lastClickedNote.fretNumber,
        false,
        false
      );
    }
  }
  @Input() set forcePlayStop(force: boolean) {
    if (force) {
      this.playTimers.forEach((timer) => {
        clearTimeout(timer);
      });
    }
  }
  @Input() set playAndDisplayPosition(frettab: Fret[]) {
    if (!this.autoPlay) {
      return;
    }
    this.lastClickedNote = {
      note: -1,
      fretNumber: -1,
      str: -1
    };
    this.setAllNotes(false);
    const tab: {str: number, fret: number}[] = [];
    frettab.forEach((fret: Fret, index) => {
      if (fret.fretNumber !== -1) {
        tab.push({
          str: index,
          fret: fret.fretNumber
        });
      }
    });
    this.playFretTab(tab);
  }
  @Input() showIntervalsOption = '';
  @Input() showAllNotesOption = '';
  @Input() showAllNotes = false;
  // ===========================================================
  // ===========================================================
  // OUTPUTS
  // ===========================================================
  // ===========================================================
  // In case of click on close tuner button
  @Output() closeTunerEvent = new EventEmitter<boolean>();
  // In case of click on select a knwown tuning
  @Output() selectTunerEvent = new EventEmitter<boolean>();
  // All cases of detune: new tuning or detune or reset one string
  @Output() detuneEvent = new EventEmitter<boolean>();
  // Click on a fret
  @Output() NoteClickEvent = new EventEmitter<ClickedNote>();
  // Click release
  @Output() NoteOffEvent = new EventEmitter<ClickedNote>();
  // One note played
  @Output() PlayedEvent = new EventEmitter<ClickedNote>();
  // Sequence of notes has been autoPlayed
  @Output() sequencePlayed = new EventEmitter<number[]>();
  // Click for toggle
  @Output() ToggleNoteEvent = new EventEmitter<number[][]>();
  // In toggle mode, in case of selection change
  @Output() ChangeSelectionEvent = new EventEmitter<number[]>();
  // In toggle mode, in case of selection change, just send last fret selected (useful for chord matching)
  @Output() ChangeSelectionLastFretOnlyEvent = new EventEmitter<number[]>();
  // New display value
  @Output() displayNotesOrIntervalsChangeEvent = new EventEmitter<string>();
  // New allNotes value
  @Output() allNotesChangeEvent = new EventEmitter<boolean>();
  // ===========================================================
  // ===========================================================

  constructor(
    public stringsService: StringsService,
    public notesService: NotesService,
    public scalesService: ScalesService,
    public chordsService: ChordsService,

    public playService: PlayService,
    public tunings: TuningsService
  ) {
    this.initIntervals();
  }
  ngOnInit() {
  }
  initStringTabs() {
    this.detuneStrings = [];
    for (let str = 0; str < this.lnbStrings; str++ ) {
      const detune = this.ltuning[str] - this.ldefaultTuning[str];
      this.detuneStrings.push({
        css: {},
        tunerCss: {},
        value: detune,
        display: this.getDetuneDisplay(detune)
      });
    }
    this.noteSelected = [];
    for (let str = 0; str < this.lnbStrings; str++) {
      const oneString: number[] = [];
      for (let fret = 0; fret < this.numberOfFrets; fret++) {
        oneString.push(-1);
      }
      this.noteSelected.push(oneString);
    }
    if (this.withToggle) {
      this.returnAllSelection();
    }
  }
  initIntervals() {
    this.intervalsDisplay = ['', '', '', '', '', '', '', '', '', '', '', ''];
  }
  // ====================================================================
  // ================================== Generate ========================
  // ====================================================================

  generateFretboardElements() {
    this.initIntervals();
    if ((this.CSNote12 === -1) ||
      (this.CSType === '') ||
      (this.CSName === '')) {
      for (let i = 0; i < 12; i++) {
        this.note12Display[i] = this.lwithNotes;
      }
    } else {
      for (let i = 0; i < 12; i++) {
        this.note12Display[i] = false;
      }
      const structure = (this.CSType === 'scale')
        ? this.scalesService.getStructure(this.CSName)
        : this.chordsService.getStructure(this.CSName);
      if (typeof structure !== 'undefined') {
        this.diatonicScale = ((this.CSType === 'scale') && this.scalesService.isDiatonic(structure));
        if (this.diatonicScale) {
          this.diatonicNotes = this.scalesService.getNoteNames(
            this.CSNote12,
            this.CSName,
            this.ldoremi,
            true,
            this.withAStartNoteName);
        }
        structure.forEach((interval: number, index: number) => {
          const note12 = (this.CSNote12 + interval) % 12;
          this.note12Display[note12] = true;
          if (this.CSType === 'chord') {
            this.intervalsDisplay[note12] = this.chordsService.getIntervalName(this.CSNote12, this.CSName, note12);
          }
        });
      } else {
        console.log('Error: fretboard component called for invalid _' + this.CSName + '_ ' + this.CSType);
      }
    }
    let posX: number;
    this.origin = this.llefty ? 'right' : 'left';
    this.fretNumbers = [];
    this.frets = [];
    for (let str = 0; str < this.lnbStrings; str++ ) {
      const posY = (8 + ((this.lnbStrings - str) * this.stringHeight)) * this.lmulti;
      posX = 7 * this.lmulti;
      this.frets[str] = [new FretZone()];
      for (let fret = 0; fret < this.numberOfFrets; fret++ ) {

        const starts = [0, 46, 106, 90, 85, 82, 72.5, 70, 69, 61.5, 59, 55, 55.9, 52.2, 47.9, 48];
        const start = starts[fret];
        const end = starts[fret + 1] - 8;
        const casePos = (fret === 0) ? start : start + (end / 2) - 16;
        const caseHighlightPos = (fret === 0) ? start : start + (end / 2) - 12;

        // Fret numbers
        if ((str === 0) && (fret > 0)) {
          const fretNumber = new FretNumber();
          fretNumber.css = {
            // color: 'black',
            ...this.caseCss(0, this.lmulti + 'em', 32),
            'opacity': .5,
            'font-size': '80%'
          };
          if ([3, 5, 7, 9, 12].indexOf(fret) !== -1) {
            //fretNumber.css['box-shadow'] = 'rgb(194, 194, 222) 1px 1px 6px';
            //fretNumber.css['background-color'] = 'rgb(233, 232, 236)';
            fretNumber.css['opacity'] = '1';
            fretNumber.css['font-size'] = '100%';
          }
          fretNumber.css[this.origin] = (posX + (casePos * this.lmulti)) + 'px';
          this.fretNumbers.push(fretNumber);
        }
        // Fret 0
        if (fret === 0) {
          this.setTuningCss(str, posX, posY);
        }
        // All frets
        this.frets[str][fret] = new FretZone();
        const note12 = (this.ltuning[str] + fret) % 12;
        let display = this.ldoremi ? this.notesService.dispDo(note12) : this.notesService.disp(note12);
        if (this.diatonicScale && this.note12Display[note12]) {
          display = this.diatonicNotes[note12];
        }
        const pctFontSize = (100 - ((display.length - 1) * 10)) * this.lmulti;

        let dotX = posX + (start + ((end) / 2) - 8) * this.lmulti;
        const clickablePosX = posX + (start * this.lmulti);
        const clickableWidth = (fret === 0) ? (32 * this.lmulti) : (end * this.lmulti);

        this.frets[str][fret] = {
          note: this.ltuning[str] + fret,
          note12,
          display,
          cssNote: this.caseCss(posY, pctFontSize + '%', 32),
          cssHighlight: this.caseCss(posY, pctFontSize + '%', 24),
          cssClickable: this.caseCss(posY, pctFontSize + '%', clickableWidth),
          cssDot: this.dotCss(posY)
        };
        if (fret === 12) {
          dotX -= (5 * this.lmulti);
        }
        if (fret === 13) {
          dotX -= (42 * this.lmulti);
        }
        const gap = this.llefty ? 4 : 3;
        this.frets[str][fret].cssNote[this.origin] = (posX + (casePos * this.lmulti) - ((fret === 0) ? gap : 0)) + 'px';
        this.frets[str][fret].cssHighlight[this.origin] = (posX + (caseHighlightPos * this.lmulti) - ((fret === 0) ? gap : 0)) + 'px';
        this.frets[str][fret].cssClickable[this.origin] = clickablePosX + 'px';
        this.frets[str][fret].cssDot[this.origin] = dotX + 'px';
        posX = clickablePosX;
      }
    } // Next string
    // Image

    this.fretboardCss = this.getFretboardCss();

    // Add margin for tuners
    let tuningGap = this.lwithDetuneStrings ? 40 : 13;
    if (this.lwithTuners) {
      tuningGap += this.tunerWidth;
    }
    this.fretboardCss['margin-' + this.origin] = (tuningGap * this.lmulti) + 'px';

    // Sizing, scrolling
    this.instruCss = {
      display: 'block',
      position: 'relative',
      'overflow-x': 'auto',
      'overflow-y': 'hidden',
      'transform-origin' : this.origin + ' top 1px',
      // 'text-align': 'center',
      'font-size': (this.ldoremi ? '13px' : '14px')
    };
  }
  toggle(thisNote: number, str: number, thisFret: number) {
    // ----------- Return the new (un)selected note
    if (this.noteSelected[str][thisFret] === thisNote) {
      this.noteSelected[str][thisFret] = -1;
    } else {
      if (this.selectOneFretByString) {
        for (let f = 0; f < this.numberOfFrets; f++) {
          this.noteSelected[str][f] = -1;
        }
      }
      this.noteSelected[str][thisFret] = thisNote;
    }
    this.ToggleNoteEvent.emit(this.noteSelected);
    this.returnAllSelection();
  }
  returnAllSelection() {
    // ----------- Return all the selected notes/fret by string
    if (!this.withToggle) {
      return;
    }
    const selectionAllFrets: number[] = [];
    const selectionLastFret: number[] = []; // Useful for chord matching
    for (let str = 0; str < this.lnbStrings; str++) {
      let foundNoteOnThisString = false;
      for (let fret = this.numberOfFrets - 1; fret >= 0; fret--) {
        const note = this.noteSelected[str][fret];
        if (note !== -1) {
          selectionAllFrets.push(note);
          if (!foundNoteOnThisString) {
            selectionLastFret.push(note);
          }
          foundNoteOnThisString = true;
        }
      }
    }
    this.ChangeSelectionEvent.emit(selectionAllFrets);
    this.ChangeSelectionLastFretOnlyEvent.emit(selectionLastFret);
  }
  note12Click(note12: number) {
    let found: FretZone;
    this.frets[0].forEach((fret: FretZone, fretNumber: number) => {
      if ((fret.note12 === note12) && (!found)) {
        found = fret;
        this.noteClick(fret.note, 0, fretNumber, false, false);
      }
    });
  }
  noteClick(
    note: number,
    str: number,
    fretNumber: number,
    touched: boolean,
    fromClick: boolean
  ) {
    if (this.scrollingNoClick) {
      return;
    }
    if (this.noTouch && touched) {
      return;
    }
    if (touched) {
      this.touchSupported = true;
    } else if (this.touchSupported) { // mousedown and touchpad is supported
                                      // => mousedown not played
      return;
    }
    this.lastClickedNote = {
      note,
      str,
      fretNumber
    };
    if (fromClick) {
      this.NoteClickEvent.emit(this.lastClickedNote);
    }
    if (this.withToggle) {
      this.toggle(note, str, fretNumber);
    }
    this.pressed = (str * 100) + fretNumber;
    if ((this.autoPlayType !== 'none') && (this.autoPlayCS !== '')) {
      this.playCSNotes(note, str, fretNumber);
    } else {
      this.playOneNote(note, str, fretNumber);
    }
  }
  notePlayEnd(
    touched: boolean
  ) {
    if (this.noTouch && touched) {
      return;
    }
    this.pressed = -1;
    this.NoteOffEvent.emit(this.lastClickedNote);
  }
  replayClickWithNewParameters() {
    if (
      (this.autoPlayType !== 'none') &&
      (this.lastClickedNote.note !== -1)
    ) {
      this.noteClick(
        this.lastClickedNote.note,
        this.lastClickedNote.str,
        this.lastClickedNote.fretNumber,
        false,
        false
      );
    }
  }
  playOneNote(
    note: number,
    str: number,
    fretNumber: number
  ) {
    const clickedNote: ClickedNote = {
      note,
      str,
      fretNumber
    };
    this.PlayedEvent.emit(clickedNote);
    if (this.sound) {
      this.sound.notePlay(note, this.lvolume);
    } else {
      this.playService.notePlay(note, this.lvolume);
    }
  }
  classForNoteByString(
    str: number,
    thisFret: number,
    note12: number
  ): string {
    if (this.lhighlightNotes12.indexOf(note12) !== -1) {
      return 'red-mark';
    }
    if (((str * 100) + thisFret) === this.pressed) {
      return 'pressed';
    }
    if (this.withToggle && (this.noteSelected[str][thisFret] !== -1)) {
      return 'selected';
    }
    if (note12 === this.CSNote12) {
      return 'root';
    }
    return '';
  }
  isSharp(note12: number): boolean {
    return ([1, 3, 6, 8, 10].indexOf(note12) !== -1);
  }
  isDisplay(value: string): boolean {
    return this.displayNotesOrIntervals === value;
  }
  toggleDisplayNotesOrIntervals() {
    this.setNotesOrIntervals((this.displayNotesOrIntervals === 'notes') ? 'intervals' : 'notes');
    this.replayClickWithNewParameters();
  }
  setNotesOrIntervals(value: string) {
    if (this.displayNotesOrIntervals === value) {
      return;
    }
    this.displayNotesOrIntervals = value;
    this.displayNotesOrIntervalsChangeEvent.emit(value);
  }
  toggleAllNotes() {
    this.setAllNotes(!this.showAllNotes);
    this.replayClickWithNewParameters();
  }
  setAllNotes(value: boolean) {
    if (this.showAllNotes === value) {
      return;
    }
    this.showAllNotes = value;
    this.allNotesChangeEvent.emit(value);
  }
  isHighlighted(str: number, fret: number): boolean {
    let found = false;
    this.lhighlightFrets.forEach((highlightFret: HighlightFret) => {
      if ((highlightFret.string === str) && (highlightFret.fret === fret)) {
        found = true;
      }
    });
    return found;
  }

  // ====================================================================
  // ================================== CSS  ============================
  // ====================================================================
  getFretboardCss() {
    let height = (60 + (this.lnbStrings * 24)) * this.lmulti;
    height += 6; // Need fix size (not with lmulti) because option font-size has a minimum !!
    const offset = ([0, 0, 0, 0, 4, 159, 337, 540, 767][this.lnbStrings] * this.lmulti) - this.upperLabels;
    const imageUrl = 'url(/assets/fretboard-imgs/rose' + ((this.llefty) ? '_lefty' : '') + '.png)';
    const imageColor = 'transparent';
    const imagePosition =  '0px -' + offset + 'px';
    const imageSize = (1040 * this.lmulti) + 'px ' + (1020 * this.lmulti) + 'px';
    const imageRepeat = 'no-repeat';
    return {
      background : imageColor + ' ' + imageUrl + ' ' + imagePosition + ' / ' + imageSize + ' ' + imageRepeat,
      height : height + 'px',
      width : (1060 * this.lmulti) + 'px',
      position: 'relative',
      display: 'inline-block'
    };
  }
  caseCss(top: number, fontSize: string, caseWidth: number) {
    return {
      top: top + 'px',
      'font-size': fontSize,
      'border-radius': (4 * this.lmulti) + 'px ',
      height: (21 * this.lmulti) + 'px',
      border: (2 * this.lmulti) + 'px solid transparent',
      'line-height': ((21 - 2) * this.lmulti) + 'px',
      width: (caseWidth * this.lmulti) + 'px',
      'margin-left': (-2 * this.lmulti) + 'px',
      'margin-bottom': (1 * this.lmulti) + 'px'
    };
  }
  dotCss(top: number) {
    return {
      top: top + (28 * this.lmulti) + 'px ',
      'border-radius': '50%',
      height: (10 * this.lmulti) + 'px',
      width: (10 * this.lmulti) + 'px',
      border: (1.6 * this.lmulti) + 'px solid #ddd',
      background: '#333',
      opacity: .6
    };
  }
  getCSSNote(fret: FretZone) {
    const css = { ...fret.cssNote };
    const borderColor = (this.lhighlightNotes12.indexOf(fret.note12) === -1)
      ? 'transparent'
      : 'red';
    css.border = (this.lmulti * 2) + 'px solid ' + borderColor;
    return css;
  }
  setTuningCss(str: number, posX: number, posY: number) {
    this.detuneStrings[str].css = {
      top: (posY + (3 * this.lmulti)) + 'px',
      width: (28 * this.lmulti) + 'px'
    };
    this.detuneStrings[str].tunerCss = {top: (posY + (3 * this.lmulti)) + 'px'};
    this.detuneStrings[str].css[this.origin] = (posX - (this.detuneWidth * this.lmulti)) + 'px';
    const detuneGap = this.lwithDetuneStrings ? this.detuneWidth : 0;
    this.detuneStrings[str].tunerCss[this.origin] = (posX - ((this.tunerWidth + detuneGap) * this.lmulti)) + 'px';
  }

  // ====================================================================
  // ================================== Tuning ==========================
  // ====================================================================
  getDetuneDisplay(value: number): string {
    let sign = '';
    if (value < 0) {
      sign = '-';
    }
    if (value > 0) {
      sign = '+';
    }
    return sign + ' ' + Math.abs(value);
  }
  changeDetune(str: number, value: any) {
    this.ltuning[str] += value;
    this.detuneStrings[str].value += value;
    this.detuneStrings[str].display = this.getDetuneDisplay(this.detuneStrings[str].value);
    this.detuneEvent.emit(true);
    this.generateFretboardElements();
  }
  resetStringTuningNoRefresh(str: number) {
    this.ltuning[str] -= this.detuneStrings[str].value;
    this.detuneStrings[str].value = 0;
    this.detuneStrings[str].display = this.getDetuneDisplay(this.detuneStrings[str].value);
  }
  resetTunings() {
    this.detuneStrings.forEach((detune, str) => {
      this.resetStringTuningNoRefresh(str);
    });
    this.detuneEvent.emit(true);
    this.generateFretboardElements();
  }
  resetStringTuning(str: number) {
    this.resetStringTuningNoRefresh(str);
    this.detuneEvent.emit(true);
    this.generateFretboardElements();
  }
  selectTuning() {
    this.selectTunerEvent.emit(true);
  }
  closeTuning() {
    this.closeTunerEvent.emit(true);
  }
  unSelectTabNotes() {
    this.frets.forEach((str) => {
      str.forEach((fret) => {
        delete fret.cssNote['background-color'];
      });
    });
  }
  getStructure(name: string) {
    return (this.autoPlayType === 'chord')
      ? this.chordsService.getStructure(name)
      : this.scalesService.getStructure(name);
  }
  playCSNotes(start: number, str: number, fret: number) {
    const structure = [ ...this.getStructure(this.autoPlayCS) ];
    if (structure) {
      // Add octave if arpeggio
      if ((this.autoPlayType === 'scale') || this.arpeggioChord) {
        structure.push(12);
      }
      // Useful even if not interval display ==> For Tonic color evaluation
      this.initIntervals();
      structure.forEach((offset) => {
        const note12 = (start + offset + 240) % 12;
        if (this.autoPlayType === 'chord') {
          this.intervalsDisplay[note12] = this.chordsService.getIntervalName(
            (start + 240) % 12,
            this.autoPlayCS,
            note12
          );
        }
      });
      if (!this.showAllNotes) {
        this.playFretTab(
          this.getStringsAndFrets(start, str, fret, structure)
        );
      } else {
        this.playAndShowAllNotes(start, structure);
      }
    }
  }
  getDelay(index: number): number {
    if ((this.autoPlayType === 'scale') || this.arpeggioChord) {
      return Math.round(index * 60000 / this.bpm);
    }
    return 0;
  }
  playAndShowAllNotes(start: number, structure: number[]) {
    this.unSelectTabNotes();
    const sequencePlayed: number[] = [];
    this.playTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.playTimers = [];
    structure.forEach((offset, index) => {
      const noteValueToPlay = start + offset;
      const note12 = (noteValueToPlay + 240) % 12;
      const playTimer = setTimeout(() => {
        this.frets.forEach((stringFrets: FretZone[]) => {
          stringFrets.forEach((fretZone: FretZone) => {
            if (fretZone.note12 === note12) {
              fretZone.cssNote['background-color'] = this.getCSColor(offset);
            }
          });
        });
        this.playOneNote(noteValueToPlay, -1, -1);
        sequencePlayed.push(noteValueToPlay);
        if (sequencePlayed.length === structure.length) {
          this.sequencePlayed.emit(sequencePlayed);
        }
      }, this.getDelay(index));
      this.playTimers.push(playTimer);
    });
  }
  getCSColor(offset: number): string {
    if ((offset % 12) === 0) {
      return this.rootColor;
    }
    return (this.autoPlayType === 'chord')
      ? this.chordColor
      : this.scaleColor;
  }
  playFretTab(tab: FretOnString[]) {
    this.unSelectTabNotes();
    const sequencePlayed: number[] = [];
    const sfd: {str: number, fret: number, delay: number, css: {}}[] = [];
    tab.forEach((c, index) => {
      if (typeof this.frets[c.str][c.fret] !== 'undefined') {
        const css = { ...this.frets[c.str][c.fret].cssNote };
        css['background-color'] = this.getCSColor(
          ((this.frets[c.str][c.fret].note % 12) === (this.lastClickedNote.note % 12)) ? 0 : 1
        );
        sfd.push({
          str: c.str,
          fret: c.fret,
          delay: this.getDelay(index),
          css
        });
      }
    });
    this.playTimers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.playTimers = [];
    sfd.forEach((o) => {
      if (o.delay > 0) {
        const playTimer = setTimeout(() => {
          this.showAndPlayOne(o.str, o.fret, o.css);
          sequencePlayed.push(this.frets[o.str][o.fret].note);
          if (sequencePlayed.length === sfd.length) {
            this.sequencePlayed.emit(sequencePlayed);
          }
        }, o.delay);
        this.playTimers.push(playTimer);
      } else {
        this.showAndPlayOne(o.str, o.fret, o.css);
        sequencePlayed.push(this.frets[o.str][o.fret].note);
        if (sequencePlayed.length === sfd.length) {
          this.sequencePlayed.emit(sequencePlayed);
        }
      }
    });
  }
  showAndPlayOne(str: number, fret: number, css: {}) {
    if (this.showAllNotes) {
    } else {
      this.frets[str][fret].cssNote = css;
    }
    this.playOneNote(
      this.frets[str][fret].note,
      str,
      fret
    );
  }
  getNextNoteFromRoot(
    fromNote: number,
    targetNote: number,
    fromString: number,
    fromFret: number
  ): {str: number, fret: number, note12: number} {
    if (fromNote === targetNote) {
      return {
        str: fromString,
        fret: fromFret,
        note12: fromNote % 12
      };
    }
    let str = -1;
    let fret = -1;
    const note12 = targetNote % 12;
    let delta = 100;
    for (let s = fromString; s < this.lnbStrings; s++) {
      const strNoteGap = this.ltuning[s] - this.ltuning[fromString];
      const newDelta = targetNote - fromNote - strNoteGap;
      const newDeltaAbs = Math.abs(newDelta);
      if (
        ((fromFret + newDelta) > -1) &&
        (newDeltaAbs < delta) &&
        (delta > 3)
      ) {
        delta = newDeltaAbs;
        str = s;
        fret = fromFret + newDelta;
      }
    }
    return {str, fret, note12};
  }
  getStringsAndFrets(
    noteStart: number,
    strStart: number,
    fretStart: number,
    structure: number[]
  ): FretOnString[] {
    const result: FretOnString[] = [];
    structure.forEach((offset) => {
      result.push(
        this.getNextNoteFromRoot(
          noteStart,
          noteStart + offset,
          strStart,
          fretStart
        )
      );
    });
    return result;
  }
  optionsStyle() {
    return {
      ...this.detuneStrings[0].tunerCss,
      margin: (26 * this.lmulti) + 'px 0'
    };
  }
  optionStyle() {
    return {
      'font-size': (.84 * this.lmulti) + 'em',
      padding: '0 ' + (6 * this.lmulti) + 'px',
      margin: '0 ' + (3 * this.lmulti) + 'px',
      'border-radius': (3 * this.lmulti) + 'px'
    };
  }
  forceDefaultTuning() {
    const tuning = [28, 33, 38, 43, 47, 52];
    if (this.lnbStrings > 6) {
      tuning.unshift(23);
    }
    if (this.lnbStrings > 7) {
      tuning.unshift(18);
    }
    return tuning;
  }
  onScroll(event: Event) {
    this.scrollingNoClick = true;
    if (this.scrollingNoClickTimer) {
      clearTimeout(this.scrollingNoClickTimer);
    }
    this.scrollingNoClickTimer = setTimeout(() => {
      this.scrollingNoClick = false;
    }, 400);
  }
}

